import { useReducer } from 'react';

export enum RatingValue {
  ThumbsDown = 1,
  ThumbsUp = 5,
}

export enum OrderReviewActions {
  SUBMIT_FORM = 'SUBMIT_FORM',
  THUMBS_DOWN = 'THUMBS_DOWN',
  THUMBS_UP = 'THUMBS_UP',
}

// TODO: get these from src/types.ts after mutations land
export interface Review {
  comment: string;
  photoUrlList: string[];
  rating: RatingValue;
}

export interface ReviewMap {
  [orderItemUUID: string]: Review;
}

export interface ReviewState {
  reviews: {
    [orderItemUUID: string]: Review;
  };
  selectedItemUUID: string | null;
}

export const emptyInitialState: ReviewState = {
  reviews: {},
  selectedItemUUID: null,
};

export const initialReview: Review = {
  comment: '',
  photoUrlList: [],
  rating: RatingValue.ThumbsUp,
};

export interface SubmitFormPayload {
  comment: string;
  photoUrls: string[];
}

interface SubmitFormAction {
  payload: SubmitFormPayload & {
    uuid: string;
  };
  type: OrderReviewActions.SUBMIT_FORM;
}

interface ThumbsDownAction {
  payload: {
    uuid: string;
  };
  type: OrderReviewActions.THUMBS_DOWN;
}

interface ThumbsUpAction {
  payload: {
    uuid: string;
  };
  type: OrderReviewActions.THUMBS_UP;
}

export type ActionType = SubmitFormAction | ThumbsDownAction | ThumbsUpAction;

const reviewsReducer = (
  state: ReviewState,
  action: ActionType,
): ReviewState => {
  let itemUUID;
  let currentReview;

  switch (action.type) {
    case OrderReviewActions.THUMBS_DOWN:
      itemUUID = action.payload?.uuid;
      currentReview = state.reviews[itemUUID] || initialReview;
      const isThumbsDown = currentReview.rating === RatingValue.ThumbsDown;
      const isOpen = isThumbsDown && state.selectedItemUUID === itemUUID;

      return {
        ...state,
        reviews: {
          ...state.reviews,
          [itemUUID]: {
            ...currentReview,
            rating: RatingValue.ThumbsDown,
          },
        },
        selectedItemUUID: isOpen ? null : itemUUID,
      };
    case OrderReviewActions.THUMBS_UP:
      itemUUID = action.payload?.uuid;
      currentReview = state.reviews[itemUUID] || initialReview;
      return {
        ...state,
        reviews: {
          ...state.reviews,
          [itemUUID]: {
            ...currentReview,
            rating: RatingValue.ThumbsUp,
          },
        },
        selectedItemUUID: null,
      };
    case OrderReviewActions.SUBMIT_FORM:
      itemUUID = action.payload?.uuid;
      return {
        ...state,
        reviews: {
          ...state.reviews,
          [itemUUID]: {
            ...state.reviews[itemUUID],
            comment: action.payload.comment,
            photoUrlList: action.payload.photoUrls,
          },
        },
        selectedItemUUID: null,
      };
    default:
      return emptyInitialState;
  }
};

export const useReviewFormReducer = (
  initialState: ReviewState = emptyInitialState,
) => useReducer(reviewsReducer, initialState);
